import {CommentsCategory} from '@domain/comments'
import {useNotification} from '@hconnect/uikit/src/common'
import {MutateOptions, useMutation} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useCallback} from 'react'
import {useErrorHandler} from 'react-error-boundary'

import {mutations} from '../../../api/mutations'
import {notifyIfErrorMessage} from '../../../common'
import {PlannerQueryClient} from '../../../components/providers/QueryProvider'
import {useTrackPlannerEvent} from '../../../helpers/trackPlannerEvents'
import {ErrorResponse, Comment} from '../../../interfaces/api'

export const useAddScheduleComment = () => {
  const {notify} = useNotification()
  const raiseError = useErrorHandler()
  const trackPlannerEvent = useTrackPlannerEvent()
  const {mutate, isPending: isAddCommentLoading} = useMutation<
    Comment,
    AxiosError<ErrorResponse>,
    Parameters<typeof mutations.addComment>[0]
  >({
    mutationFn: mutations.addComment,
    mutationKey: ['addScheduleComment'],
    onError: (error) => {
      notifyIfErrorMessage(error.response?.data.detail, raiseError, notify)
    },
    onSettled: () => {
      trackPlannerEvent({name: 'onCommentAddToScheduleItem'})
      void PlannerQueryClient.invalidateQueries({
        queryKey: ['comments', {commentsCategory: CommentsCategory.AssetOperationTimes}]
      })
    }
  })

  const addScheduleComment = useCallback(
    (
      params: Omit<Parameters<typeof mutations.addComment>[0], 'commentsCategory'>,
      options?: MutateOptions<
        Comment,
        AxiosError<ErrorResponse>,
        Parameters<typeof mutations.addComment>[0]
      >
    ) => mutate({...params, commentsCategory: CommentsCategory.AssetOperationTimes}, options),
    [mutate]
  )

  return {
    addScheduleComment,
    isAddCommentLoading
  }
}
